import { useCallback, useRef, useSyncExternalStore } from "react";
import { appState, type AppState } from "../../state";
import { onStateChange } from "../../stateBus";

// appState (state.ts) stays the single mutable source of truth for the
// whole migration - see stateBus.ts. Vanilla mutation sites that a React
// feature also reads are paired with a notifyStateChange() call; this hook
// is how React features subscribe to that.
//
// getSnapshot must be a stable function reference - passing a fresh arrow
// function on every render (`() => selector(appState)`) is a real
// correctness bug with useSyncExternalStore, not just a perf nit: it can
// silently miss updates in some components while appearing to work in
// others (caught via a real test - a bulk-select counter that read
// appState correctly but never re-rendered, while a sibling component
// reading the same state updated fine). selectorRef always holds the
// latest selector so callers can keep passing an inline arrow function.
export function useAppState<T>(selector: (s: AppState) => T): T {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const getSnapshot = useCallback(() => selectorRef.current(appState), []);

  return useSyncExternalStore(onStateChange, getSnapshot);
}
